// Centralized application configuration
// Contains API base paths, endpoints, HTTP methods, and header keys
import config from '../configs/config';

// Generic HTTP request wrapper for SyncUp Platform APIs
// Handles low-level request concerns (fetch/axios, auth, errors, parsing)
import syncUpPlatformRequest from '../repositories/syncupPlatformRequest';

/**
 * featureFlagsRepository
 * ----------------------
 * Repository layer responsible for direct communication with
 * SyncUp Platform Feature Flag APIs.
 *
 * Responsibilities:
 * - Build API requests using config
 * - Call the HTTP request wrapper
 * - Return raw API responses
 *
 * This layer intentionally contains NO business logic.
 * Any transformation, validation, or caching should be handled
 * by the service layer above this repository.
 */
const featureFlagsRepository = {
  /**
   * Fetches all feature flags for a specific SyncUp application.
   *
   * Flow:
   * UI --> Service --> Repository --> syncUpPlatformRequest --> API
   *
   * @param {Object} params
   * @param {string} params.syncUpAppName
   *   SyncUp application name passed as a custom request header
   * @param {boolean} [params.responseStatus=false]
   *   Optional flag to include full response metadata (status, headers)
   *
   * @returns {Promise<Object|Array>}
   *   Resolves with feature flag data (and optionally response status)
   */
  getFeatureFlags: async ({ syncUpAppName, responseStatus = false }) => {
    /**
     * Extract API configuration values for SyncUp Platform
     * from centralized config to avoid hardcoding.
     */
    const {
      resources,
      basePath,
      httpMethods,
      headers
    } = config.apiSettings.syncUpPlatform;

    // Execute HTTP GET request to retrieve feature flags
    return await syncUpPlatformRequest({
      /**
       * API endpoint for fetching feature flags
       */
      urlSuffix: resources.featureFlags,

      /**
       * Base path targeting Admin Drive APIs
       */
      basePath: basePath.adminDrive,

      /**
       * HTTP method used for read operations
       */
      httpMethod: httpMethods.GET,

      /**
       * Whether caller wants response metadata in addition to payload
       */
      responseStatus: responseStatus,

      /**
       * Required headers for SyncUp Platform APIs
       */
      headers: {
        // Identify which SyncUp application the request applies to
        [headers.SyncUpProduct.key]: syncUpAppName
      }
    });
  },

  /**
   * Creates a new feature flag for a SyncUp application.
   *
   * @param {Object} params
   * @param {string} params.syncUpAppName
   *   SyncUp application name passed as a request header
   * @param {Object} params.body
   *   Payload containing feature flag properties
   *   (featureId, isEnabled, description, etc.)
   *
   * @returns {Promise<Object>}
   *   API response for the created feature flag
   */
  createFeatureFlag: async ({ syncUpAppName, body }) => {
    const {
      resources,
      basePath,
      httpMethods,
      headers
    } = config.apiSettings.syncUpPlatform;

    // Execute HTTP POST request to create a feature flag
    return await syncUpPlatformRequest({
      /**
       * API endpoint for creating feature flags
       */
      urlSuffix: resources.createFeatureFlag,

      /**
       * Base path targeting Admin Drive APIs
       */
      basePath: basePath.adminDrive,

      /**
       * HTTP method used for create operations
       */
      httpMethod: httpMethods.POST,

      /**
       * Required headers for SyncUp Platform APIs
       */
      headers: {
        // Identify the SyncUp application context
        [headers.SyncUpProduct.key]: syncUpAppName
      },

      /**
       * Request payload sent to the API
       */
      body
    });
  },

  /**
   * Deletes an existing feature flag.
   *
   * @param {Object} params
   * @param {string} params.featureId
   *   Unique identifier of the feature flag to delete
   * @param {string} params.syncUpAppName
   *   SyncUp application name passed as a request header
   *
   * @returns {Promise<void>}
   *   Resolves when deletion is successful
   */
  deleteFeatureFlag: async ({ featureId, syncUpAppName }) => {
    const {
      resources,
      basePath,
      httpMethods,
      headers
    } = config.apiSettings.syncUpPlatform;

    // Execute HTTP DELETE request to remove the feature flag
    return await syncUpPlatformRequest({
      /**
       * API endpoint for deleting feature flags.
       * Feature ID is injected into the URL template.
       */
      urlSuffix: resources.deleteFeatureFlag.replace(
        ':featureflagName',
        featureId
      ),

      /**
       * Base path targeting Admin Drive APIs
       */
      basePath: basePath.adminDrive,

      /**
       * HTTP method used for delete operations
       */
      httpMethod: httpMethods.DELETE,

      /**
       * Required headers for SyncUp Platform APIs
       */
      headers: {
        // Identify the SyncUp application context
        [headers.SyncUpProduct.key]: syncUpAppName
      }
    });
  }
};

export default featureFlagsRepository;
